import { AlertCircle } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Code } from "../atoms/Code";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

/** Plain, reassuring fallback shown to non-technical users by default. */
const DEFAULT_MESSAGE =
	"Something went wrong loading this page. Please try again.";

export interface ErrorStateProps {
	title?: string;
	message?: string;
	onRetry?: () => void;
	error?: Error;
	/**
	 * Reveal the raw technical `error.message` in a collapsible section for
	 * debugging. Defaults to false so public users only see the friendly copy.
	 */
	showDetails?: boolean;
	className?: string;
}

function ErrorState({
	title = "Something went wrong",
	message,
	onRetry,
	error,
	showDetails = false,
	className,
}: ErrorStateProps): ReactNode {
	return (
		<div
			className={cn(
				"flex flex-col items-center justify-center gap-4 py-16 text-center",
				className,
			)}
		>
			<AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
			<Heading size="lg">{title}</Heading>
			<Text textColor="mutedForeground" className="max-w-sm">
				{message ?? DEFAULT_MESSAGE}
			</Text>
			{showDetails && error && (
				<details className="max-w-sm text-left">
					<summary className="cursor-pointer text-xs text-mutedForeground">
						Technical details
					</summary>
					<Code inline={false} className="mt-2 text-left text-xs">
						{error.message}
					</Code>
				</details>
			)}
			{onRetry && (
				<Button variant="outline" onClick={onRetry}>
					Try again
				</Button>
			)}
		</div>
	);
}
ErrorState.displayName = "ErrorState";

export default ErrorState;
export { ErrorState };
